import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="31159" className="hidden md:flex gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu className="hidden md:flex">
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative px-4 py-2 text-sm font-medium text-foreground transition-colors hover:text-secondary">
                {item.label}
                <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-primary transition-all duration-300 ease-out group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full bg-background/95 backdrop-blur-xl border-b border-border shadow-xl md:hidden animate-in slide-in-from-top-5 fade-in duration-200">
      <nav className="container mx-auto flex flex-col p-6 gap-4">
        {loading ? <div className="flex flex-col gap-4">
            {[1, 2, 3].map(i => <div key={i} className="h-8 w-full bg-muted animate-pulse rounded-md" />)}
          </div> : menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-lg font-medium text-foreground hover:text-secondary transition-colors py-2 border-b border-border/50 last:border-0" onClick={onClose} data-index={i}>
              {item.label}
            </Link>)}
        <div className="pt-4 mt-2 border-t border-border">
          <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-sans tracking-tight" size="lg">
            Book Consultation
          </Button>
        </div>
      </nav>
    </div>;
}
export default function SiteHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="шапка-сайта" className={cn("sticky top-0 z-50 w-full transition-all duration-300 ease-in-out", isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border shadow-md py-3" : "bg-background py-5")}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between">
          {}
          <div className="flex-shrink-0">
            <Link to="/HomePage" className="block">
              <img src="https://welcome-husky.10web.cloud/wp-content/uploads/2026/03/Логотип-Адвокат-Кондрашов.jpg" alt="Attorney Nikolay Kondrashov" className="h-8 w-auto object-contain" />
            </Link>
          </div>

          {}
          <div className="hidden md:flex flex-1 justify-center px-8">
            <MenuProvider menu_id="16">
              <DesktopMenu />
            </MenuProvider>
          </div>

          {}
          <div className="hidden md:flex flex-shrink-0 items-center gap-4">
            <span className="hidden lg:block font-mono text-xs text-secondary tracking-widest uppercase">Адвокатский кабинет №40/806 КОндрашова николая евгеньевича</span>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg transition-transform hover:scale-[1.02] font-sans tracking-tight rounded-sm">Записаться на консультацию</Button>
          </div>

          {}
          <div className="md:hidden">
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-foreground hover:bg-accent hover:text-accent-foreground" aria-label="Toggle menu">
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      {}
      <MenuProvider menu_id="16">
        <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      </MenuProvider>
    </header>;
}